import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Container,
  FormField,
  Header,
  Input,
  Modal,
  Select,
  SpaceBetween,
  StatusIndicator,
  Table,
  TextFilter,
  type SelectProps,
  type TableProps,
} from '@cloudscape-design/components';
import {
  fetchFulfillmentQueue,
  triggerFulfillment,
  type FulfillmentRequest,
  type ProvisionResult,
} from '../api/fulfillment';
import { fetchTenants, type Tenant } from '../api/tenants';

const STATUS_MAP: Record<string, 'pending' | 'in-progress' | 'success' | 'error'> = {
  received: 'pending', resolving: 'in-progress', resolved: 'in-progress',
  provisioning: 'in-progress', completed: 'success', failed: 'error',
};

export function FulfillmentQueue() {
  const navigate = useNavigate();
  const [items, setItems] = useState<FulfillmentRequest[]>([]);
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [filterText, setFilterText] = useState('');
  const [loading, setLoading] = useState(true);

  // New fulfillment modal
  const [showNew, setShowNew] = useState(false);
  const [selectedTenant, setSelectedTenant] = useState<SelectProps.Option | null>(null);
  const [productCode, setProductCode] = useState<string>('aeos-pc3');
  const [companyName, setCompanyName] = useState('');
  const [customerEmail, setCustomerEmail] = useState('');
  const [awsAccountId, setAwsAccountId] = useState('');
  const [mpCustomerId, setMpCustomerId] = useState('');
  const [isNewTenant, setIsNewTenant] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitResult, setSubmitResult] = useState<ProvisionResult | null>(null);

  useEffect(() => {
    loadQueue();
    fetchTenants().then((res) => setTenants(res.tenants)).catch(() => {});
  }, []);

  async function loadQueue() {
    setLoading(true);
    try { const data = await fetchFulfillmentQueue(); setItems(data.requests); }
    catch (err) { console.error('Failed to load:', err); }
    finally { setLoading(false); }
  }

  function openNew() {
    setSelectedTenant(null);
    setIsNewTenant(false);
    setProductCode('aeos-pc3');
    setCompanyName('');
    setCustomerEmail('');
    setAwsAccountId('');
    setMpCustomerId('');
    setSubmitResult(null);
    setShowNew(true);
  }

  function handleTenantSelect(option: SelectProps.Option) {
    if (option.value === '__new__') {
      setSelectedTenant(option);
      setIsNewTenant(true);
      setCompanyName('');
      setCustomerEmail('');
      setAwsAccountId('');
    } else {
      setSelectedTenant(option);
      setIsNewTenant(false);
      const t = tenants.find((t) => t.tenantId === option.value);
      if (t) {
        setCompanyName(t.companyName);
        setCustomerEmail(t.contactEmail);
        setAwsAccountId(t.awsAccountId || '');
      }
    }
  }

  async function handleSubmit() {
    setSubmitting(true);
    try {
      const result = await triggerFulfillment({
        marketplaceCustomerId: mpCustomerId || `manual-${Date.now()}`,
        productCode,
        customerEmail,
        companyName,
        awsAccountId: awsAccountId || undefined,
      });
      setSubmitResult(result);
      await loadQueue();
    } catch (err) { console.error('Failed:', err); }
    finally { setSubmitting(false); }
  }

  const tenantOptions: SelectProps.Options = [
    { label: '+ Create New Tenant', value: '__new__', description: 'New customer — will create tenant record' },
    ...tenants.map((t) => ({
      label: `${t.name} — ${t.companyName}`,
      value: t.tenantId,
      description: t.awsAccountId ? `AWS: ${t.awsAccountId}` : 'No AWS account',
    })),
  ];

  const filtered = items.filter((item) => {
    if (!filterText) return true;
    const s = filterText.toLowerCase();
    return (item.companyName || '').toLowerCase().includes(s) ||
      (item.customerEmail || '').toLowerCase().includes(s) ||
      item.marketplaceCustomerId.toLowerCase().includes(s) ||
      item.productCode.toLowerCase().includes(s) || item.status.includes(s);
  });

  const COLUMNS: TableProps.ColumnDefinition<FulfillmentRequest>[] = [
    { id: 'company', header: 'Company', cell: (item) => item.companyName || '—', width: 150 },
    { id: 'email', header: 'Contact', cell: (item) => item.customerEmail || '—', width: 170 },
    { id: 'product', header: 'Product', cell: (item) => {
      const names: Record<string, string> = { 'aeos-pc3': 'AEOS PC3', 'acp': 'ACP', 'one-ct-wafr': 'One-CT WAFR', 'archera': 'Archera', '2ndfactors': '2ndFactors' };
      return names[item.productCode] || item.productCode;
    }, width: 120 },
    {
      id: 'status', header: 'Status', width: 120,
      cell: (item) => <StatusIndicator type={STATUS_MAP[item.status] ?? 'pending'}>{item.status}</StatusIndicator>,
    },
    {
      id: 'tenant', header: 'Tenant', width: 110,
      cell: (item) => item.tenantId ? <span style={{ fontFamily: 'monospace', fontSize: 11 }}>{item.tenantId.slice(0, 8)}…</span> : '—',
    },
    { id: 'received', header: 'Received', cell: (item) => new Date(item.receivedAt).toLocaleString(), width: 160 },
    {
      id: 'actions', header: 'Next Step', width: 180,
      cell: (item) => {
        if (item.status === 'completed' && item.tenantId) {
          return <Button variant="primary" onClick={() => navigate(`/ops/deployments/${item.tenantId}`)}>Deploy Roles →</Button>;
        }
        if (item.status === 'failed') return <StatusIndicator type="error">{item.errorMessage || 'Failed'}</StatusIndicator>;
        return <StatusIndicator type="loading">Processing</StatusIndicator>;
      },
    },
  ];

  return (
    <SpaceBetween size="l">
      <Header variant="h1" description="Marketplace events and manual fulfillment requests. Creates tenants and starts the onboarding flow."
        actions={
          <SpaceBetween direction="horizontal" size="xs">
            <Button iconName="refresh" onClick={loadQueue} loading={loading}>Refresh</Button>
            <Button variant="primary" onClick={openNew}>New Fulfillment Request</Button>
          </SpaceBetween>
        }
      >Fulfillment</Header>

      <Table
        columnDefinitions={COLUMNS} items={filtered} loading={loading}
        loadingText="Loading..." trackBy="requestId" variant="full-page"
        stickyHeader stripedRows
        empty={<Box textAlign="center" color="inherit"><b>No requests</b><Box variant="p">Click "New Fulfillment Request" to start onboarding a customer.</Box></Box>}
        filter={<TextFilter filteringText={filterText} filteringPlaceholder="Search..." onChange={({ detail }) => setFilterText(detail.filteringText)} />}
        header={<Header counter={`(${filtered.length})`}>Requests</Header>}
      />

      <Modal visible={showNew} onDismiss={() => setShowNew(false)} header="New Fulfillment Request"
        footer={<Box float="right"><SpaceBetween direction="horizontal" size="xs">
          <Button variant="link" onClick={() => setShowNew(false)}>Cancel</Button>
          {submitResult ? (
            <Button variant="primary" onClick={() => navigate(`/ops/deployments/${submitResult.tenantId}`)}>Deploy Roles →</Button>
          ) : (
            <Button variant="primary" loading={submitting} disabled={!selectedTenant && (!companyName || !customerEmail)} onClick={handleSubmit}>Submit</Button>
          )}
        </SpaceBetween></Box>}>
        <SpaceBetween size="m">
          {submitResult && (
            <Alert type="success">
              Tenant created: <code>{submitResult.tenantId.slice(0, 8)}…</code> — Click "Deploy Roles →" to continue.
            </Alert>
          )}
          {!submitResult && (<>
            <FormField label="Tenant" description="Select existing tenant or create new">
              <Select selectedOption={selectedTenant} onChange={({ detail }) => handleTenantSelect(detail.selectedOption)}
                options={tenantOptions} placeholder="Select or create tenant..." filteringType="auto" />
            </FormField>
            {(isNewTenant || !selectedTenant) && (<>
              <FormField label="Company Name"><Input value={companyName} onChange={({ detail }) => setCompanyName(detail.value)} placeholder="Acme Corp" /></FormField>
              <FormField label="Contact Email"><Input value={customerEmail} onChange={({ detail }) => setCustomerEmail(detail.value)} placeholder="admin@acme.com" type="email" /></FormField>
              <FormField label="AWS Account ID" description="Optional"><Input value={awsAccountId} onChange={({ detail }) => setAwsAccountId(detail.value)} placeholder="123456789012" /></FormField>
            </>)}
            {selectedTenant && !isNewTenant && (
              <Alert type="info">Using existing tenant: {selectedTenant.label}</Alert>
            )}
            <FormField label="Product">
              <Select selectedOption={{ label: productCode === 'aeos-pc3' ? 'AEOS PC3' : productCode === 'acp' ? 'ACP' : productCode === 'one-ct-wafr' ? 'One-CT WAFR/FTR' : productCode === 'archera' ? 'Archera' : productCode, value: productCode }}
                onChange={({ detail }) => setProductCode(detail.selectedOption.value || 'aeos-pc3')}
                options={[
                  { label: 'AEOS PC3', value: 'aeos-pc3', description: 'Partner Central 3 migration & channel management' },
                  { label: 'ACP', value: 'acp', description: 'Agentic Control Panel — managed digital workforce' },
                  { label: 'One-CT WAFR/FTR', value: 'one-ct-wafr', description: 'Well-Architected & Foundational Technical Review' },
                  { label: 'Archera', value: 'archera', description: 'Insurance-based RI purchasing & cost optimization' },
                  { label: '2ndFactors', value: '2ndfactors', description: 'Authentication & identity management' },
                ]} />
            </FormField>
            <FormField label="Marketplace Customer ID" description="Leave blank for manual fulfillment">
              <Input value={mpCustomerId} onChange={({ detail }) => setMpCustomerId(detail.value)} placeholder="Auto-generated if blank" />
            </FormField>
          </>)}
        </SpaceBetween>
      </Modal>
    </SpaceBetween>
  );
}
